import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { useQuery } from '@apollo/client'
import { CircularProgress, Stack, Tooltip, Typography } from '@mui/material'

import { useUser } from 'store'
import { useNavigate } from 'router'
import ActionModal from 'components/Modal/ActionModal'
import { toast } from 'components/global/CUK/Toast/Toast'
import CardActionsButtonsGroup from 'components/global/CardActions/CardActionsButtonsGroup'
import CardActionButton from 'components/global/CardActions/CardActionButton'
import { Code, Site, CopyIcon } from './CardStyles'
import { OPERATION_FIND_COUPON_STANDARD } from '../operations-gql/queries'

import FirstUse, { PeriodUse, Validity } from './Widgets'
import CardPreview, { CardBlockInfo, CardBody, CardHeader } from 'packages/ui/CardPreview'
import { Status } from '../Table/config-columns-standard'
import usePdf from '../use-pdf'
import { trackEvent } from 'analytics'

export const ModalPrint = ({onClose, onClick}) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)

  const handleAnswer = async withOffer => {
    setLoading(true)
    try {
      await onClick(withOffer)
    } finally {
      setLoading(false)
    }
  }

  return (
    <ActionModal
      cancelLabel={t('ui_elements.no')}
      confirmColor="primary"
      confirmLabel={t('ui_elements.yes')}
      description={(
        <Typography
          component="span"
          sx={{ fontSize: 13, textAlign: 'center' }}
          dangerouslySetInnerHTML={{ __html: t('coupons.actions.print_code.modal.description') }}
        />
      )}
      icon={<i className="fa-light fa-print" />}
      loading={loading}
      onCancel={() => handleAnswer(true)}
      onClose={onClose}
      onConfirm={() => handleAnswer(false)}
      title={t('coupons.actions.print_code.modal.title')}
      variant="primary"
    />
  )
}

export const PrintButton = ({ coupon, hasBorder = true }) => {
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation()
  const [showModal, setShowModal] = useState(false)
  const { handlePrintCode } = usePdf()

  return (
    <>
      {
        showModal && (
          <ModalPrint
            onClose={() => setShowModal(false)}
            onClick={async (withOffer) => {
              await handlePrintCode({coupon, withOffer})
              trackEvent('print_code', { action_scope: 'item' })
              setShowModal(false)
            }}
          />
        )
      }
      <CardActionButton
        tooltiptitle={t('coupons.actions.print_code.button_tooltip')}
        iconlabel={loading ? <CircularProgress size={14} /> : 'print'}
        iconsize="14px"
        onClick={async (e) => {
          e.stopPropagation()
          e.preventDefault();
          if (coupon.offer) {
            setShowModal(true)
          } else {
            setLoading(true)
            await handlePrintCode({coupon, withOffer: false})
            trackEvent('print_code', { action_scope: 'item' })
            setLoading(false)
          }
        }}
        style={{ ...(hasBorder && { border: '1px solid #E5E7EB', borderRadius: '8px', padding: '8px' }) }}
      />
    </>

  )
}

export const PrintNotebookButton = ({ coupon, hasBorder = true }) => {
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation()
  const { handlePrintNotebook } = usePdf()
  const [showModal, setShowModal] = useState(false)

  return (
    <>
      {showModal && (
        <ModalPrint
          onClose={() => setShowModal(false)}
          onClick={async withOffer => {
            await handlePrintNotebook({ coupon, withOffer })
            trackEvent('print_code_notebook', { action_scope: 'item' })
            setShowModal(false)
          }}
        />
      )}
      <CardActionButton
        tooltiptitle={t('coupons.actions.print_notebook.button_tooltip')}
        iconlabel={loading ? <CircularProgress size={14} /> : 'notebook'}
        iconsize="14px"
        onClick={async e => {
          e.stopPropagation()
          e.preventDefault()
          if (coupon.offer) {
            setShowModal(true)
          } else {
            setLoading(true)
            await handlePrintNotebook({coupon, withOffer: false})
            trackEvent('print_code_notebook', { action_scope: 'item' })
            setLoading(false)
          }
        }}
        style={{ ...(hasBorder && { border: '1px solid #E5E7EB', borderRadius: '8px', padding: '8px' }) }}
      />
    </>
  )
}

const Header = ({ coupon }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [user] = useUser()
  const { findCouponByCorrelationId, error } = usePdf(coupon, t)
  const [correlationCount, setCorrelationCount] = useState(0)

  useEffect(() => {
    const fetchCorrelationCount = async () => {
      try {
        const { data } = await findCouponByCorrelationId({
          variables: { findCouponByCorrelationIdId: coupon.correlation_id }
        })

        const correlation =
          data?.findCouponByCorrelationId.filter(coupon => coupon.available === true || coupon.available_soon > 0) || []

        setCorrelationCount(correlation.length)
      } catch (err) {
        console.error('Error fetching correlation data:', err)
      }
    }

    if (coupon.correlation_id) {
      fetchCorrelationCount()
    }
  }, [coupon, findCouponByCorrelationId])

  const handleCopyCode = () => {
    navigator.clipboard.writeText(coupon.code)
    trackEvent('copy_code', { code_type: 'standard' })
    toast.success(t('coupons.actions.copy_code.toast_message'))
  }

  if (error) {
    return toast.error(t('coupons.actions.print_code.error'))
  }

  return (
    <CardHeader>
      <Stack direction="column" alignItems="center" gap={1}>
        <Status coupon={coupon}/>
        <Site>{coupon.site.name}</Site>
        <Tooltip title={t('coupons.actions.copy_code.tooltip_message')} arrow>
          <Code onClick={handleCopyCode}>
            <CopyIcon className="fa-light fa-copy" />
            <span>{coupon.code}</span>
          </Code>
        </Tooltip>
      </Stack>
      {(coupon.available_soon > 0 || coupon.available || (coupon.used && !coupon.expired)) && (
        <CardActionsButtonsGroup
          style={{
            display: 'flex',
            gap: '20px',
            backgroundColor: 'white',
            marginTop: 10
          }}
        >
          <CardActionButton
            tooltiptitle={t('coupons.actions.send_code.button_tooltip')}
            iconlabel={'message-sms'}
            onClick={() => {
              trackEvent('send_code_button', { code_type: 'standard' })
              navigate({ pathname: `${location.pathname}/send-sms` })
            }}
            style={{
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              padding: '8px'
            }}
          />
          <PrintButton coupon={coupon} />
          {correlationCount > 1 && <PrintNotebookButton coupon={coupon} />}
          <CardActionButton
            tooltiptitle={t('coupons.actions.desactivate_code.button_tooltip')}
            iconlabel={'pause'}
            iconsize="14px"
            onClick={() => {
              if (!user.demo_mode) {
                trackEvent('disable_code_button', { code_type: 'standard' })
                navigate({ pathname: `${location.pathname}/disable-code` })
              }
            }}
            style={{ border: '1px solid #E5E7EB', borderRadius: '8px', padding: '8px' }}
          />
        </CardActionsButtonsGroup>
      )}
    </CardHeader>
  )
}

const Body = ({ coupon, localDateOptions }) => {
  const { t } = useTranslation()
  const devicesNumber = coupon.number_of_devices === -1 
    ? t('coupons.card.infinite_usage') 
    : coupon.number_of_devices

  return (
    <CardBody grid>
      <CardBlockInfo title={t('coupons.card.offer')} subtitle={coupon.offer?.name} />
      {coupon.offer?.name && (
        <CardBlockInfo title={t('coupons.card.validity')} subtitle={<Validity coupon={coupon} />} />
      )}
      <CardBlockInfo
        title={t('coupons.card.first_provisioning_date')}
        subtitle={<FirstUse coupon={coupon} localDateOptions={localDateOptions} />}
      />
      <CardBlockInfo title={t('coupons.card.period')} subtitle={<PeriodUse coupon={coupon} />} />
      <CardBlockInfo
        title={t('coupons.card.creation_date')}
        subtitle={new Date(coupon.created_at).toLocaleDateString(i18next.language)}
      />
      <CardBlockInfo
        title={t('coupons.card.created_by')}
        subtitle={
          coupon.creator_firstname && coupon.creator_lastname
            ? `${coupon.creator_firstname} ${coupon.creator_lastname}`
            : coupon.created_by || '-'
        }
      />
      <CardBlockInfo title={t('coupons.card.devices_number')} subtitle={devicesNumber} />
      <CardBlockInfo
        title={t('coupons.card.private_wifi', 'Wifi privé')}
        subtitle={coupon.with_ran_access ? t('ui_elements.enabled', 'Activé') : t('ui_elements.disabled', 'Non activé')}
      />
      <CardBlockInfo title={t('coupons.card.phone')} subtitle={coupon.phone} />
      <CardBlockInfo title={t('coupons.card.description')} subtitle={coupon.description} />
    </CardBody>
  )
}

const CardStandard = ({ id, localDateOptions, isModal = false }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { loading, error, data } = useQuery(OPERATION_FIND_COUPON_STANDARD, {
    variables: { id: +id, langue: localStorage.i18nextLng }
  })

  return (
    <CardPreview
      title={t('coupons.card.title')}
      loading={loading}
      error={error ? t('shared.error') : null}
      onClose={() => navigate({ pathname: '..' })}
      hideHeader={isModal}
      components={
        !isModal && data && !(data.coupon.expired || data.coupon.disabled)
          ? {
              ActionButtons: () => (
                <CardActionButton
                  tooltiptitle={t('coupons.actions.edit.button_tooltip')}
                  iconlabel={'pen'}
                  onClick={() => {
                    trackEvent('edit_code_button', { code_type: 'standard' })
                    navigate({ pathname: `${location.pathname}/edit` })
                  }}
                />
              )
            }
          : {}
      }
    >
      {data && (
        <>
          <Header coupon={data.coupon} isModal={isModal} />
          <Body coupon={data.coupon} localDateOptions={localDateOptions} />
        </>
      )}
    </CardPreview>
  )
}

export default CardStandard
